import { useEffect, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useQuery } from 'react-query'
import { useNavigate } from 'react-router-dom'

import {
    IconLiveCamera,
    IconLiveConnection,
    IconLiveMicrophone,
    IconSortAsc,
    IconStartLiveCarret,
    IconStartLiveViewers
} from 'assets/svg/sprite'
import { Button } from 'components/Button/Button'
import CheckboxField from 'components/Form/CheckboxField/CheckboxField'
import SearchField from 'components/Form/SearchField/SearchField'
import SortModal from 'components/UI/Modal/Sort/SortModal'
import { RadioButton, SwitchButton } from 'components-ref/atoms'
import FansList from 'features/FansList/FansList'
import { emitter } from 'services/emitter/emitter'
import { EventsEmitter } from 'services/emitter/events'
import { getProfileGroups } from 'services/endpoints/social'
import {
    type MediaStreamOptions,
    SETTINGS,
    useStreamingStore
} from 'store/streaming'
import { type IGroup } from 'types/interfaces/ITypes'

import LiveModal from '../LiveModal'
import './_startModal.scss'

const connectionsList = [
    { name: 'Web RTC' },
    { name: 'Low latency' },
    { name: 'TCP' }
]
interface StartModalProps {
  streamingReady: boolean
}

const StartModal = ({ streamingReady }: StartModalProps) => {
    const settingsType = useStreamingStore(state => state.settingsType)
    const setSettingsType = useStreamingStore(state => state.setSettingsType)
    const {
        description,
        setDescription,
        cameraList,
        microphoneList,
        setCameraList,
        setMicrophoneList,
        setActiveCamera,
        setActiveMicrophone,
        activeCamera,
        activeMicrophone,
        groups,
        setGroups
    } = useStreamingStore()
    const { t } = useTranslation()
    const navItems = [t('availableTo'), 'Exclude']
    const showTips = useStreamingStore(state => state.showTips)
    const setShowTips = useStreamingStore(state => state.setShowTips)
    const [selectedTab, setSelectedTab] = useState(navItems[0])

    const [title, setTitle] = useState('')

    const [searchTerm, setSearchTerm] = useState('')

    const [filter, setFilter] = useState('numberOfFans')
    const [sort, setSort] = useState('asc')

    const [groupAllFans, setGroupAllFans] = useState<{
        avatars: string[]
        count: number
    }>({
        avatars: [],
        count: 0
    })

    const [initialGroups, setInitialGroups] = useState<IGroup[]>([])
    const [filteredGroups, setFilteredGroups] = useState<IGroup[]>([])

    const navigate = useNavigate()

    const { data } = useQuery('allGroups', getProfileGroups, {
        refetchOnWindowFocus: false,
        enabled: streamingReady
    })

    useEffect(() => {
        const executeFn = async () => {
            const devices = await navigator.mediaDevices.enumerateDevices()

            const videoDevices = devices.filter(
                d => d.kind === 'videoinput'
            ) as MediaStreamOptions[]

            const audioDevices = devices.filter(
                d => d.kind === 'audioinput'
            ) as MediaStreamOptions[]

            setCameraList(videoDevices)
            console.log(videoDevices)
            if (videoDevices.length > 0) {
                setActiveCamera({
                    label: videoDevices[0].label,
                    deviceId: videoDevices[0].deviceId
                })
            }
            setMicrophoneList(audioDevices)
            if (audioDevices.length > 0) {
                setActiveMicrophone({
                    label: audioDevices[0].label,
                    deviceId: audioDevices[0].deviceId
                })
            }
        }
        executeFn()
    }, [])

    useEffect(() => {
        if (data) {
            let count = 0
            const avatars: string[] = data
                .map(group => {
                    if (group.users) {
                        count = count + group.users.length
                        return group.users.map(user => user.avatar.url)
                    } else return []
                })
                .flat()

            setGroupAllFans({ avatars, count })
            setInitialGroups(data)

            const validFilteredGroups = data.filter(
                item => item.name !== 'All' && item.name !== 'Expired'
            )
            setFilteredGroups(validFilteredGroups)
        }
    }, [data])

    useEffect(() => {
    const base = initialGroups ?? [];

    const searched = searchTerm
        ? base.filter(g =>
            g.name.toLowerCase().includes(searchTerm.toLowerCase())
        )
        : base;

    const validFilteredGroups = searched.filter(
        item => item.name !== 'All' && item.name !== 'Expired'
    );

    setFilteredGroups(validFilteredGroups);
    }, [initialGroups, searchTerm]);

    useEffect(() => {
        if (settingsType === SETTINGS.DEFAULT) {
            setTitle('')
        }
        if (settingsType === SETTINGS.AVAILABILITY) {
            setTitle(t('liveStreamAvailability'))
        }
        if (settingsType === SETTINGS.CAMERA) {
            setTitle(t('cameraAndMicrophone'))
        }
        if (settingsType === SETTINGS.CONNECTION) {
            setTitle(t('chooseConnectionType'))
        }
        if (settingsType === SETTINGS.SORT_MODAL) {
            setTitle(t('sortBy'))
        }
    }, [settingsType])

    const handleGroupClick = (group: { id: string; name: string }) => {
        // if (selectedGroups.includes(group)) {
        //     const tmp = selectedGroups.filter(el => el !== group)
        //     if (tmp.length > 0) {
        //         setSelectedGroups(tmp)
        //     } else {
        //         setSelectedGroups(['allFans'])
        //     }
        // } else {
        //     const tmp = selectedGroups.filter(el => el !== 'allFans')
        //     setSelectedGroups([...tmp, group])
        // }
        setGroups([group])
    }

    const handleCloseModal = () => {
        if (settingsType === SETTINGS.DEFAULT) {
            const portal = document.getElementById('portal')
            if (portal) {
                portal?.classList.contains('portal--open') &&
                    portal.classList.remove('portal--open')
            }
            navigate(-1)
        } else if (settingsType === SETTINGS.SORT_MODAL) {
            setSettingsType(SETTINGS.AVAILABILITY)
        } else {
            setSettingsType(SETTINGS.DEFAULT)
        }
    }

    const handleChangeDescription = (
        e: React.ChangeEvent<HTMLTextAreaElement>
    ) => {
        let newText = e.target.value
        if (newText.length > 500) {
            newText = newText.slice(0, 500)
        }
        setDescription(newText)
    }

    return (
        <LiveModal
            hideCloseButton
            title={title}
            onClose={handleCloseModal}
            type='start'
            showConnectionSettings={() => {
                setSettingsType(SETTINGS.CONNECTION)
            }}
            customClass={
                settingsType !== SETTINGS.DEFAULT ? 'additionalSettings' : ''
            }
        >
            {settingsType === SETTINGS.DEFAULT && (
                <div className='startmodal'>
                    <div className='startmodal__top box'>
                        <div className='startmodal__top--left'>
                            <IconStartLiveViewers />
                            <span>{t('availableTo')}</span>
                        </div>
                        <div className='startmodal__top--right'>
                            <span>{groups[0].name}</span>
                            <div
                                className='startmodal__top__iconwrapper'
                                onClick={() => {
                                    setSettingsType(SETTINGS.AVAILABILITY)
                                }}
                            >
                                <IconStartLiveCarret />
                            </div>
                        </div>
                    </div>
                    <div className='startmodal__mid'>
                        <div className='customTextarea box'>
                            <textarea
                                name='streamDescription'
                                id='streamDescription'
                                rows={4}
                                value={description}
                                onChange={handleChangeDescription}
                                placeholder={t('addStreamDescription')}
                            ></textarea>
                        </div>
                        <div className='char-num'>
                            <span>{description.length}</span> / 500{' '}
                            {t('characters')}
                        </div>
                    </div>
                    <div className='startmodal__bot'>
                        <div className='box'>
                            <div className='switchWithLabel'>
                                <div className='switchWithLabel__text'>
                                    {t('showCollectedTipsToViewers')}
                                </div>
                                    <SwitchButton
                                    active={showTips}
                                    toggle={() => {
                                        setShowTips(!showTips)
                                    }}
                                    />
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {settingsType === SETTINGS.AVAILABILITY && (
                <div className='availabilitySettings'>
                    <div className='tabnav'>
                        {navItems.map(item => {
                            return (
                                <div
                                    key={item}
                                    className={`tabnav__item ${
                                        selectedTab === item ? 'active' : ''
                                    }`}
                                    onClick={() => {
                                        setSelectedTab(item)
                                    }}
                                >
                                    {item}
                                </div>
                            )
                        })}
                    </div>
                    <div className='availabilitySettings__searchbar'>
                        <SearchField
                            customClass='dark'
                            value={searchTerm}
                            changeFn={term => {
                                setSearchTerm(term)
                            }}
                            clearFn={() => {
                                setSearchTerm('')
                            }}
                            additionalProps={{ placeholder: t('searchGroups') }}
                        />
                        <div
                            className='availabilitySettings__searchbar--sort'
                            onClick={() => {
                                setSettingsType(SETTINGS.SORT_MODAL)
                            }}
                        >
                            <IconSortAsc color='#fff' />
                        </div>
                    </div>
                    <div className='availabilitySettings__groups'>
                        <div className='availabilitySettings__groups--top'>
                            <div className='availabilitySettings__group'>
                                <FansList
                                    key={'groupFans'}
                                    title={t('all')}
                                    fans={groupAllFans}
                                    customClass='dark'
                                />
                                <CheckboxField
                                    id='All'
                                    value='All'
                                    label=''
                                    checked={groups.some(
                                        item => item.name === 'All'
                                    )}
                                    changeFn={() =>
                                        !groups.some(
                                            item => item.name === 'All'
                                        ) && setGroups([{ id: 'all', name: 'All' }])
                                    }
                                />
                            </div>
                        </div>
                        <div className='availabilitySettings__groups--divider'></div>
                        <div className='availabilitySettings__groups--bottom'>
                            {filteredGroups.map(group => (
                                <div
                                    key={group.name}
                                    className='availabilitySettings__group'
                                >
                                    <FansList
                                        key={'filteredFans'}
                                        title={group.name}
                                        fans={{
                                            avatars: group.users
                                                ? group.users.map(
                                                      user => user.avatar.url
                                                  )
                                                : [],
                                            count: group.users
                                                ? group.users.length
                                                : 0
                                        }}
                                        customClass='dark'
                                    />
                                    <CheckboxField
                                        id={group.name}
                                        value={group.name}
                                        label=''
                                        checked={groups.some(
                                            item => item.id === group.id
                                        )}
                                        changeFn={() => {
                                            handleGroupClick({
                                                name: group.name,
                                                id: group.id
                                            })
                                        }}
                                    />
                                </div>
                            ))}
                        </div>
                        <div className='availabilitySettings__buttons'>
                            <Button
                                text={t('reset')}
                                color='slate-blue'
                                font='mont-14-normal'
                                type='transparent--borderless'
                                width='fit'
                                height='3'
                                padding='1-15'
                                clickFn={() => {
                                    setGroups([{ id: 'all', name: 'All' }])
                                }}
                            />
                            <Button
                                text={t('apply')}
                                clickFn={() => {
                                    setSettingsType(SETTINGS.DEFAULT)
                                }}
                                color='transparent'
                                font='mont-14-normal'
                                type='transparent--blue'
                                width='10'
                                height='3'
                            />
                        </div>
                    </div>
                </div>
            )}
            {settingsType === SETTINGS.CAMERA && (
                <div className='cameraSettings'>
                    <div className='cameraSettings__list'>
                        <p className='cameraSettings__title'>Camera</p>
                        {cameraList.map((camera, idx) => {
                            return (
                                <div className='camera' key={idx}>
                                    <div className='camera__content'>
                                        <div className='camera__content--icon'>
                                            <IconLiveCamera />
                                        </div>
                                        <div className='camera__content--text'>
                                            {camera.label}
                                        </div>
                                    </div>
                                    <RadioButton
                                        clickFn={() => {
                                            setActiveCamera(camera)
                                            emitter.emit(
                                            EventsEmitter.LIVE_CHANGE_CAMERA,
                                            camera
                                            )
                                        }}
                                        active={
                                            camera.deviceId ===
                                            activeCamera?.deviceId
                                        }
                                    />
                                </div>
                            )
                        })}
                        <p className='cameraSettings__title'>Microphone</p>
                        {microphoneList.map((microphone, idx) => {
                            return (
                                <div className='camera' key={idx}>
                                    <div className='camera__content'>
                                        <div className='camera__content--icon'>
                                            <IconLiveMicrophone />
                                        </div>
                                        <div className='camera__content--text'>
                                            {microphone.label}
                                        </div>
                                    </div>
                                    <RadioButton
                                        clickFn={() => {
                                            setActiveMicrophone(microphone)
                                            emitter.emit(
                                                EventsEmitter.LIVE_CHANGE_MICROPHONE,
                                                microphone
                                            )
                                        }}
                                        active={
                                            microphone.deviceId ===
                                            activeMicrophone?.deviceId
                                        }
                                    />
                                </div>
                            )
                        })}
                    </div>
                    <div className='cameraSettings__button'>
                        <Button
                            text={t('apply')}
                            clickFn={() => {
                                setSettingsType(SETTINGS.DEFAULT)
                            }}
                            color='transparent'
                            font='mont-14-normal'
                            type='transparent--blue'
                            width='14'
                            height='3'
                        />
                    </div>
                </div>
            )}
            {settingsType === SETTINGS.CONNECTION && (
                <div className='connectionSettings'>
                    <div className='connectionSettings__list'>
                        {connectionsList.map((connection, idx) => {
                            return (
                                <div className='connection' key={idx}>
                                    <div className='connection__content'>
                                        <div className='connection__content--icon'>
                                            <IconLiveConnection />
                                        </div>
                                        <div className='connection__content--text'>
                                            {connection.name}
                                        </div>
                                    </div>
                                    <RadioButton active={idx === 0} />
                                </div>
                            )
                        })}
                    </div>
                    <div className='cameraSettings__button'>
                        <Button
                            text={t('apply')}
                            color='transparent'
                            font='mont-14-normal'
                            type='transparent--blue'
                            width='14'
                            height='3'
                        />
                    </div>
                </div>
            )}
            {settingsType === SETTINGS.SORT_MODAL && (
                <div className='sortmodal'>
                    <SortModal
                        elements={{
                            first_section: [
                                {
                                    value: 'numberOfFans',
                                    name: t('numberOfFans')
                                },
                                { value: 'name', name: t('name') },
                                { value: 'dateAdded', name: t('dateAdded') }
                            ],
                            second_section: [
                                { value: 'asc', name: t('ascending') },
                                { value: 'desc', name: t('descending') }
                            ]
                        }}
                        applyFn={(filterVal: string, sortVal: string) => {
                            setFilter(filterVal)
                            setSort(sortVal)
                            setSettingsType(SETTINGS.AVAILABILITY)
                        }}
                        filter={filter}
                        sort={sort}
                    />
                </div>
            )}
        </LiveModal>
    )
}

export default StartModal
